import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterListIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { getAssignedTasks, updateTask, getProperty } from '../services/propertyService';

interface AssignedTasksPanelProps {
  open: boolean;
  onClose: () => void;
  agencySlug: string;
  propertyId: string;
  propertyAddress?: string;
  canEdit: boolean;
  onPropertyUpdate?: (updatedProperty: any) => void;
}

const AssignedTasksPanel: React.FC<AssignedTasksPanelProps> = ({
  open,
  onClose,
  agencySlug,
  propertyId,
  propertyAddress,
  canEdit,
  onPropertyUpdate
}) => {
  const [assignedTasks, setAssignedTasks] = useState<any[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('');
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);

  // Fetch assigned tasks
  useEffect(() => {
    if (open && agencySlug && propertyId) {
      fetchAssignedTasks();
    }
  }, [open, agencySlug, propertyId, taskStatusFilter]);

  const fetchAssignedTasks = async () => {
    setTasksLoading(true);
    setTasksError(null);
    try {
      const response = await getAssignedTasks(agencySlug, propertyId, taskStatusFilter || undefined);
      setAssignedTasks(response.tasks || []);
    } catch (error: any) {
      console.error('Error fetching assigned tasks:', error);
      setTasksError(error.response?.data?.error || 'Failed to load tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  // Handle task status update
  const handleTaskStatusUpdate = async (taskId: string, workflowId: string, stageIndex: number, taskIndex: number, newStatus: string) => {
    setUpdatingTaskId(taskId);
    try {
      await updateTask(agencySlug, propertyId, workflowId, stageIndex, taskIndex, { status: newStatus });
      
      // Refresh the assigned tasks
      await fetchAssignedTasks();
      
      // Also refresh the property to update any workflow state
      if (onPropertyUpdate) {
        const updatedProperty = await getProperty(agencySlug, propertyId);
        onPropertyUpdate(updatedProperty);
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      setTasksError(error.response?.data?.error || 'Failed to update task status');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  // Get status icon for task
  const getTaskStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlineIcon color="success" />;
      case 'in_progress':
        return <PlayCircleOutlineIcon color="warning" />;
      case 'pending':
      default:
        return <RadioButtonUncheckedIcon color="action" />;
    }
  };

  // Get status color for task
  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'pending':
      default:
        return 'default';
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '500px', md: '600px' },
          boxSizing: 'border-box'
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'white'
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon />
              My Assigned Tasks
            </Typography>
            {propertyAddress && (
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                {propertyAddress}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Task Status Filter */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="action" />
            <TextField
              select
              size="small"
              value={taskStatusFilter}
              onChange={(e) => setTaskStatusFilter(e.target.value)}
              label="Filter by Status"
              sx={{ minWidth: 200, bgcolor: 'white' }}
            >
              <MenuItem value="">All Tasks</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </TextField>
            {taskStatusFilter && (
              <Typography variant="body2" color="text.secondary">
                {assignedTasks.length} task{assignedTasks.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Tasks List */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {tasksLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : tasksError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {tasksError}
            </Alert>
          ) : assignedTasks.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {assignedTasks.map((taskData) => {
                const taskId = `${taskData.workflowId}-${taskData.stageIndex}-${taskData.taskIndex}`;
                return (
                  <Card 
                    key={taskId}
                    sx={{ 
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: 2
                      }
                    }}
                  >
                    <CardContent>
                      {/* Task Header */}
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          {getTaskStatusIcon(taskData.task.status)}
                          <Typography variant="subtitle1" sx={{ fontWeight: 500, flex: 1, minWidth: 0 }}>
                            {taskData.task.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={taskData.workflowName}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={taskData.stageName}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                            <Chip
                              label={taskData.task.status.charAt(0).toUpperCase() + taskData.task.status.slice(1)}
                              color={getTaskStatusColor(taskData.task.status) as any}
                              size="small"
                            />
                          </Box>
                        </Box>
                        
                        {/* Status Update Control */}
                        {canEdit && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <TextField
                              select
                              size="small"
                              value={taskData.task.status}
                              onChange={(e) => handleTaskStatusUpdate(
                                taskId,
                                taskData.workflowId,
                                taskData.stageIndex,
                                taskData.taskIndex,
                                e.target.value
                              )}
                              disabled={updatingTaskId === taskId}
                              sx={{ minWidth: 150 }}
                              label="Update Status"
                            >
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="in_progress">In Progress</MenuItem>
                              <MenuItem value="completed">Completed</MenuItem>
                            </TextField>
                          </Box>
                        )}
                      </Box>
                      
                      {taskData.task.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {taskData.task.description}
                        </Typography>
                      )}
                      
                      {/* Task Details */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        {taskData.task.dueDate && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ScheduleIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              Due: {new Date(taskData.task.dueDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </Typography>
                          </Box>
                        )}
                        {taskData.task.estimatedDuration && (
                          <Typography variant="caption" color="text.secondary">
                            Duration: {taskData.task.estimatedDuration}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: 200,
              textAlign: 'center',
              p: 3
            }}>
              <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Tasks Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {taskStatusFilter ? `No ${taskStatusFilter} tasks assigned to you` : 'No tasks assigned to you for this property'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default AssignedTasksPanel;

